/**
 * Transient world effects for Calling abilities.
 *
 * Everything here is pooled and unlit: rings, the hijack leash and the Pulse through-wall
 * reveal. Nothing allocates per frame and nothing needs a second render pass — the reveal is
 * a set of `depthTest: false` sprites, which is the cheapest honest way to draw "through the
 * wall" on both WebGPU and the WebGL2 fallback.
 *
 * What used to be here and is gone: `dustBurst` (kicked-up regolith sprites) and `hazard`
 * (the Verger's burning floor panel). Weather, smoke, dust and environment damage were cut
 * from the game with the pivot away from the extraction shooter — see render/sky.ts's header.
 * `AbilityEffects.update`'s signature is unchanged.
 */

import {
  AdditiveBlending,
  BoxGeometry,
  Color,
  Mesh,
  MeshBasicNodeMaterial,
  RingGeometry,
  Sprite,
  SpriteMaterial,
  type Scene,
} from 'three/webgpu';
import type { Vec3 } from '@stillsun/shared';

const AMBER = 0xffa040;
const EMBER = 0xff6a2a;
const HYMN = 0xdfe8ff;

/** Pulse marks live this long (M3-DESIGN: 4 s, extended to 8 s by a Shade node). */
const REVEAL_MS = 4000;
const RING_SECONDS = 0.9;
const RING_MAX_SCALE_MULT = 1;
/** Hard cap on simultaneous reveal sprites, so a busy room cannot stall a phone. */
const MAX_REVEALS = 48;

interface Ring {
  mesh: Mesh;
  mat: MeshBasicNodeMaterial;
  t: number;
  radius: number;
}

/** A live leash from a Tinker to the Hymn they hijacked. */
interface Leash {
  mesh: Mesh;
  mat: MeshBasicNodeMaterial;
  casterId: number;
  targetId: number;
  until: number;
}

/** Something the Pulse should paint a silhouette on. */
export interface RevealPoint {
  pos: Vec3;
  /** Roughly how tall the thing is, so the sprite sits over its chest. */
  height: number;
  /** True for Wakers (Hymn-white); false for Hymns (amber). */
  friendly: boolean;
}

export class AbilityEffects {
  private readonly scene: Scene;
  private readonly rings: Ring[] = [];
  private readonly leashes: Leash[] = [];
  private readonly reveals: Sprite[] = [];
  private revealMats: SpriteMaterial[] = [];
  /** performance.now() the Pulse window closes at; 0 when no pulse is up. */
  private revealUntil = 0;

  private ringGeo: RingGeometry | null = null;
  private leashGeo: BoxGeometry | null = null;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------ casts

  /** Pulse: one expanding ring on the floor, then a 4 s through-wall reveal window. */
  pulse(pos: Vec3, radius: number): void {
    this.ring(pos, Math.max(4, radius), HYMN);
    this.revealUntil = performance.now() + REVEAL_MS;
  }

  /** Suppression Field / Fabricator: a dome-footprint ring, amber. */
  ring(pos: Vec3, radius: number, color: number = AMBER): void {
    const r = this.takeRing();
    r.mat.color.setHex(color);
    r.mesh.position.set(pos.x, pos.y + 0.06, pos.z);
    r.radius = radius;
    r.t = 0;
    r.mesh.scale.setScalar(0.05);
    r.mesh.visible = true;
  }

  /** Hijack: an amber leash from the Tinker to their Hymn until the duration expires. */
  hijackLeash(casterId: number, targetId: number, durationMs: number): void {
    const l = this.takeLeash();
    l.casterId = casterId;
    l.targetId = targetId;
    l.until = performance.now() + durationMs;
    l.mesh.visible = true;
  }

  get revealActive(): boolean {
    return this.revealUntil > performance.now();
  }

  // ------------------------------------------------------------------ frame

  /**
   * `points` is a caller-owned scratch buffer of which only the first `pointCount`
   * entries are live — the host skips filling it entirely when no Pulse is up, and
   * nothing here allocates. `positionOf` resolves an id for the live hijack leashes.
   */
  update(
    dt: number,
    points: readonly RevealPoint[],
    pointCount: number,
    positionOf: (id: number) => Vec3 | null,
  ): void {
    const now = performance.now();

    for (const r of this.rings) {
      if (!r.mesh.visible) continue;
      r.t += dt;
      const k = r.t / RING_SECONDS;
      if (k >= 1) {
        r.mesh.visible = false;
        continue;
      }
      r.mesh.scale.setScalar(0.05 + k * r.radius * RING_MAX_SCALE_MULT);
      r.mat.opacity = (1 - k) * 0.8;
    }

    for (const l of this.leashes) {
      if (!l.mesh.visible) continue;
      const a = positionOf(l.casterId);
      const b = positionOf(l.targetId);
      if (now > l.until || !a || !b) {
        l.mesh.visible = false;
        continue;
      }
      const ax = a.x;
      const ay = a.y + 1.3;
      const az = a.z;
      const bx = b.x;
      const by = b.y + 1.3;
      const bz = b.z;
      const dx = bx - ax;
      const dy = by - ay;
      const dz = bz - az;
      const len = Math.hypot(dx, dy, dz);
      if (len < 0.05) {
        l.mesh.visible = false;
        continue;
      }
      l.mesh.position.set((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);
      l.mesh.scale.set(1, 1, len);
      l.mesh.lookAt(bx, by, bz);
      l.mat.opacity = 0.35 + 0.25 * Math.sin(now * 0.008);
    }

    // Pulse reveal: one billboard per revealed thing, drawn over the geometry.
    const active = this.revealActive;
    const n = active ? Math.min(pointCount, points.length, MAX_REVEALS) : 0;
    for (let i = 0; i < this.reveals.length; i++) {
      const sprite = this.reveals[i];
      if (!sprite) continue;
      sprite.visible = i < n;
    }
    if (n > 0) {
      // Fade the whole set out over the last second of the window.
      const left = this.revealUntil - now;
      const fade = Math.min(1, Math.max(0, left / 1000));
      for (let i = 0; i < n; i++) {
        const p = points[i];
        if (!p) continue;
        const sprite = this.takeReveal(i);
        sprite.position.set(p.pos.x, p.pos.y + p.height * 0.6, p.pos.z);
        sprite.scale.set(0.7, p.height * 0.9, 1);
        const mat = this.revealMats[i];
        if (mat) {
          mat.color.setHex(p.friendly ? HYMN : EMBER);
          mat.opacity = 0.55 * fade;
        }
        sprite.visible = true;
      }
    }
  }

  clear(): void {
    for (const r of this.rings) r.mesh.visible = false;
    for (const l of this.leashes) l.mesh.visible = false;
    for (const s of this.reveals) s.visible = false;
    this.revealUntil = 0;
  }

  dispose(): void {
    this.clear();
    for (const r of this.rings) {
      this.scene.remove(r.mesh);
      r.mat.dispose();
    }
    for (const l of this.leashes) {
      this.scene.remove(l.mesh);
      l.mat.dispose();
    }
    for (const s of this.reveals) this.scene.remove(s);
    for (const m of this.revealMats) m.dispose();
    this.rings.length = 0;
    this.leashes.length = 0;
    this.reveals.length = 0;
    this.revealMats.length = 0;
    this.ringGeo?.dispose();
    this.leashGeo?.dispose();
  }

  // ------------------------------------------------------------------ pools

  private takeRing(): Ring {
    for (const r of this.rings) if (!r.mesh.visible) return r;
    this.ringGeo ??= new RingGeometry(0.86, 1, 40);
    const mat = new MeshBasicNodeMaterial({
      color: new Color(AMBER),
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const mesh = new Mesh(this.ringGeo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 6;
    mesh.visible = false;
    this.scene.add(mesh);
    const ring: Ring = { mesh, mat, t: 0, radius: 1 };
    this.rings.push(ring);
    return ring;
  }

  private takeLeash(): Leash {
    for (const l of this.leashes) if (!l.mesh.visible) return l;
    // A unit-length box along +Z, so `lookAt` plus a z scale is the whole placement.
    this.leashGeo ??= new BoxGeometry(0.035, 0.035, 1);
    const mat = new MeshBasicNodeMaterial({
      color: new Color(AMBER),
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      depthTest: false,
      blending: AdditiveBlending,
    });
    const mesh = new Mesh(this.leashGeo, mat);
    mesh.renderOrder = 12;
    mesh.visible = false;
    this.scene.add(mesh);
    const leash: Leash = { mesh, mat, casterId: 0, targetId: 0, until: 0 };
    this.leashes.push(leash);
    return leash;
  }

  /** Reveal sprites are indexed rather than searched: one per revealed thing per frame. */
  private takeReveal(index: number): Sprite {
    const existing = this.reveals[index];
    if (existing) return existing;
    const mat = new SpriteMaterial({
      color: new Color(EMBER),
      transparent: true,
      opacity: 0.55,
      // The whole point of a recon ping: these draw through walls.
      depthTest: false,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const sprite = new Sprite(mat);
    sprite.renderOrder = 20;
    sprite.visible = false;
    this.scene.add(sprite);
    this.reveals[index] = sprite;
    this.revealMats[index] = mat;
    return sprite;
  }
}
